import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Eye, EyeOff, Globe, LockKeyhole, Moon, ScanLine, Server, ShieldCheck, Sun,
  UserRound, WifiOff
} from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { setLocale } from '@/lib/i18n';
import { useTheme } from '@/lib/theme';
import { Button } from '@/components/ui/button';
import { Field, Input, Select } from '@/components/ui/input';

interface TerminalOption { code: string; name: string; kind: string }
type ServerStatus = 'checking' | 'online' | 'offline';

export function Login() {
  const { t, i18n } = useTranslation();
  const { login, loginBadge } = useAuth();
  const { theme, toggle } = useTheme();
  const [mode, setMode] = useState<'password' | 'badge'>('password');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [badgeCode, setBadgeCode] = useState('');
  const [terminalCode, setTerminalCode] = useState('');
  const [terminals, setTerminals] = useState<TerminalOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverStatus, setServerStatus] = useState<ServerStatus>('checking');

  useEffect(() => {
    api<{ data: TerminalOption[] }>('/auth/terminals')
      .then((result) => setTerminals(result.data))
      .catch(() => setTerminals([]));
  }, []);

  useEffect(() => {
    let active = true;
    fetch('/api/health', { credentials: 'include' })
      .then((response) => { if (active) setServerStatus(response.ok ? 'online' : 'offline'); })
      .catch(() => { if (active) setServerStatus('offline'); });
    return () => { active = false; };
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (mode === 'password') {
        await login({ identifier, password, terminalCode: terminalCode || undefined });
      } else {
        await loginBadge({ badgeCode, terminalCode: terminalCode || undefined });
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = mode === 'password' ? Boolean(identifier && password) : Boolean(badgeCode);

  return (
    <div
      className="relative flex min-h-full items-center justify-center overflow-hidden bg-[#05080f] px-4 py-10 lg:px-10"
      style={{ backgroundImage: "url('/login-bg.svg')", backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#05080f]/65 via-[#05080f]/20 to-[#05080f]/70" />

      <div className="absolute right-4 top-4 z-20 flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 p-1 backdrop-blur">
        <button
          type="button"
          onClick={() => setLocale(i18n.language === 'es' ? 'en' : 'es')}
          className="rounded-md p-2 text-slate-300 transition hover:bg-white/10 hover:text-white"
          aria-label={t('actions.language')}
        >
          <Globe className="size-4" />
        </button>
        <button
          type="button"
          onClick={toggle}
          className="rounded-md p-2 text-slate-300 transition hover:bg-white/10 hover:text-white"
          aria-label={t('actions.theme')}
        >
          {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </button>
      </div>

      <div className="relative z-10 grid w-full max-w-6xl items-center gap-10 lg:grid-cols-[1fr_420px] lg:gap-20">
        <div className="hidden min-h-[520px] flex-col justify-between lg:flex">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex size-11 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-teal-400 text-lg font-bold text-white shadow-xl shadow-blue-900/40">E</div>
              <div>
                <p className="text-[15px] font-semibold leading-5 text-white">Eurobath <span className="text-blue-300">ERP</span></p>
                <p className="text-[11px] leading-4 text-slate-400">Sistema de gestión industrial</p>
              </div>
            </div>

            <div className="mt-20 max-w-sm border-l border-blue-300/20 pl-5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-blue-300/70">Sistema corporativo</p>
              <p className="mt-3 text-lg font-medium leading-7 text-white">Acceso unificado a la operación de fábrica</p>
              <p className="mt-2 text-sm leading-6 text-slate-400">
                Compras, almacén, producción, embalaje y expedición con trazabilidad por unidad.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-white/10 pt-5 text-[10px] text-slate-500">
            <span>© 2026 Eurobath</span>
            <span>v1.0.0 · Fase 1</span>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[420px]">
          <div className="mb-6 flex items-center gap-3 lg:hidden">
            <div className="flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-teal-400 font-bold text-white">E</div>
            <div>
              <p className="text-sm font-semibold text-white">Eurobath ERP</p>
              <p className="text-[11px] text-slate-400">Sistema de gestión industrial</p>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-card/95 p-6 shadow-2xl shadow-black/50 backdrop-blur-xl sm:p-7">
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-foreground">{t('login.title')}</h2>
              <p className="mt-1 text-xs text-muted-foreground">{t('login.subtitle')}</p>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-1 rounded-lg border border-border bg-muted/50 p-1">
              <button
                type="button"
                onClick={() => { setMode('password'); setError(null); }}
                className={`flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition ${mode === 'password' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <UserRound className="size-3.5" /> {t('login.withPassword')}
              </button>
              <button
                type="button"
                onClick={() => { setMode('badge'); setError(null); }}
                className={`flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition ${mode === 'badge' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <ScanLine className="size-3.5" /> {t('login.withBadge')}
              </button>
            </div>

            <form onSubmit={submit} className="mt-5 space-y-4">
              {mode === 'password' ? (
                <>
                  <Field label={t('login.user')} required>
                    <div className="relative">
                      <UserRound className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        autoComplete="username"
                        autoFocus
                        className="h-11 pl-9"
                        placeholder="usuario o email"
                      />
                    </div>
                  </Field>
                  <Field label={t('login.password')} required>
                    <div className="relative">
                      <LockKeyhole className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        className="h-11 pl-9 pr-10"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((value) => !value)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      >
                        {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                      </button>
                    </div>
                  </Field>
                </>
              ) : (
                <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4">
                  <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
                    <ScanLine className="size-4 text-primary" /> {t('login.badgeHint')}
                  </div>
                  <Field label={t('login.badge')} required>
                    <Input value={badgeCode} onChange={(e) => setBadgeCode(e.target.value)} placeholder="BDG-ALM-01" autoFocus className="h-11 font-mono" />
                  </Field>
                </div>
              )}

              <Field label={t('login.terminal')} hint="Si lo dejas en automático se asigna el terminal de tu rol.">
                <Select value={terminalCode} onChange={(e) => setTerminalCode(e.target.value)} className="h-11">
                  <option value="">Asignación automática</option>
                  {terminals.map((terminal) => (
                    <option key={terminal.code} value={terminal.code}>{terminal.code} · {terminal.name}</option>
                  ))}
                </Select>
              </Field>

              {error ? (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                  {error}
                </div>
              ) : null}

              <Button type="submit" className="h-11 w-full justify-between px-4" loading={loading} disabled={!canSubmit}>
                <span>{t('login.submit')}</span>
                {!loading ? <ArrowRight className="size-4" /> : null}
              </Button>
            </form>

            <div className="mt-5 flex items-center justify-between gap-3 border-t border-border pt-4 text-[10px] text-muted-foreground">
              <span className={`flex items-center gap-1.5 ${serverStatus === 'online' ? 'text-emerald-600 dark:text-emerald-400' : serverStatus === 'offline' ? 'text-red-600 dark:text-red-400' : ''}`}>
                {serverStatus === 'online' ? <Server className="size-3" /> : serverStatus === 'offline' ? <WifiOff className="size-3" /> : <Server className="size-3 animate-pulse" />}
                {serverStatus === 'online' ? 'Servidor operativo' : serverStatus === 'offline' ? 'Sin conexión' : 'Comprobando…'}
              </span>
              <span className="flex items-center gap-1.5"><ShieldCheck className="size-3" /> Conexión segura</span>
            </div>
          </div>

          <p className="mt-4 text-center text-[10px] text-slate-500">Acceso restringido · Las operaciones quedan registradas en auditoría</p>
        </div>
      </div>
    </div>
  );
}
